import Vue from 'vue';
import Api from 'services/Api';
import { debounce } from 'lodash';

const progress = new Vue({
  data() {
    return {
      duration: 0,
      lastUpdated: new Date(),
      loaders: [],
    };
  },

  computed: {
    total() {
      return this.loaders.length;
    },

    loading() {
      return this.total - this.completed;
    },

    progress() {
      return this.total > 0 ? this.completed / this.total : 0;
    },

    completed() {
      return this.loaders.reduce(
        (total, loader) => total + (loader.complete ? 1 : 0),
        0
      );
    },

    succeeded() {
      return this.loaders.reduce(
        (total, loader) => total + (loader.complete && loader.success ? 1 : 0),
        0
      );
    },

    failed() {
      return this.completed - this.succeeded;
    },
  },

  methods: {
    /**
     * Wraps Api methods to capture start and end of loads
     *
     * @param targets
     */
    init(...targets) {
      targets.forEach(target => {
        Object.keys(target).forEach(key => {
          if (target[key] instanceof Function) {
            target[key] = this.wrap(target[key]);
          }
        });
      });
    },

    wrap(fn) {
      const add = this.add.bind(this);
      const end = this.end.bind(this);
      return function(...args) {
        const loader = add(...args);
        return fn(...args)
          .then(res => {
            end(loader, true);
            return res;
          })
          .catch(err => {
            end(loader, false);
            throw err;
          });
      };
    },

    add(args) {
      if (this.total === 0) {
        this.intervalId = window.setInterval(
          () => (this.duration += 0.25),
          250
        );
      }
      const loader = new Loader(args);
      this.loaders.push(loader);
      this.$emit('progress', this.progress, loader);
      return loader;
    },

    end(loader, success) {
      loader.complete = true;
      loader.success = success;
      loader.duration = Date.now() - loader.start;
      this.lastUpdated = new Date();
      this.$emit('progress', this.progress, loader);
      this.checkFinished();
    },

    checkFinished: debounce(function() {
      if (this.progress === 1) {
        this.$emit('finish');
        this.reset();
      }
    }, 500),

    reset() {
      this.loaders = [];
      this.duration = 0;
      window.clearInterval(this.intervalId);
    },
  },
});

function Loader(args) {
  this.args = args;
  this.complete = false;
  this.success = false;
  this.start = new Date();
  this.duration = 0;
}

progress.init(Api, Api.insights);

export default progress;
